import { useMemo } from 'react';
import type { Column } from '../../Table/Table';
import { UserRank } from '../components';
import { UserLevelDetails } from '../../../lib/enums/userLevels';
import { formatNumberWithCommas } from '../../../lib/numbers/formatNumberWithComma';
import { Icon, IconType } from '../../Props/Icon/Icon';
import styled from 'styled-components';
import type { LabelledToggleOption } from '../../Toggle/LabelledToggle/LabelledToggle';
import { BadgeList } from '../../Props/BadgeList/BadgeList';
import { Tooltip } from '../../Tooltip';
import { LevelTooltip } from '../components';
import type { LeaderboardUser, LeaderboardDepartment } from '../../../lib/types/LeaderboardUser';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';
import { AvatarInitials } from '../../AvatarInitials';
import { useMeQuery } from '../../../apiHooks/useMeQuery';
import ExternalLinkButton from '../../Button/ExternalLinkButton/ExternalLinkButton';
import { LeaderboardTypeParam } from '../../../apiHooks/useLeaderboardQuery';
import { Ellipsis } from '../../utility/Ellipsis';

type LeaderboardData = {
  columns: Column<LeaderboardUser | LeaderboardDepartment>[];
  mobileColumns: Column<LeaderboardUser | LeaderboardDepartment>[];
  scopeFilterLabels: LabelledToggleOption<LeaderboardTypeParam>[];
};

export const useLeaderboardData = (selectedFilterScope?: LeaderboardTypeParam): LeaderboardData => {
  const { data: meData } = useMeQuery();

  return useMemo(
    () => ({
      scopeFilterLabels: [
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="myTeam" />,
          value: LeaderboardTypeParam.TEAM,
          selected: selectedFilterScope === LeaderboardTypeParam.TEAM,
        },
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="myDepartment" />,
          value: LeaderboardTypeParam.DEPARTMENT,
          selected: selectedFilterScope === LeaderboardTypeParam.DEPARTMENT,
        },
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="myCompany" />,
          value: LeaderboardTypeParam.COMPANY,
          selected: selectedFilterScope === LeaderboardTypeParam.COMPANY,
        },
      ],
      columns:
        selectedFilterScope === LeaderboardTypeParam.DEPARTMENT
          ? [
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="rank" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <UserRank rank={department.rank} previousRank={department.previousRank} />;
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="departments" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <UserName>{department.department}</UserName>;
                },
                alignment: 'left',
                width: '2fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="points" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <UserPoints className="user-points">
                      {formatNumberWithCommas(department.averageScore)}{' '}
                      <Icon width={20} height={20} icon={IconType.COIN} />
                    </UserPoints>
                  );
                },
                alignment: 'right',
                width: '150px',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="managerTeamBehaviors" subKey="teamSize" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <div style={{ textAlign: 'center' }}>{department.learnerCount}</div>;
                },
                alignment: 'center',
                width: '100px',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="badges" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <BadgeList badgeIds={department.badges ?? []} />;
                },
                alignment: 'left',
                width: '6fr',
              },
            ]
          : [
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="rank" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return <UserRank rank={userData.rank} previousRank={userData.previousRank} />;
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="name" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <UserName>
                      {userData.avatar ? (
                        <UserImg src={`data:image/png;base64,${userData.avatar}`} />
                      ) : (
                        <AvatarInitials name={userData.name || ''} id={userData.name ?? ''} size="32px" />
                      )}
                      <Ellipsis>{userData.name}</Ellipsis>
                    </UserName>
                  );
                },
                alignment: 'left',
                width: '2fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="badges" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <BadgeList
                      badgeIds={userData.badges ?? []}
                      renderLinks={() => (
                        <ExternalLinksContainer>
                          {!!userData.badges?.length && userData.learnerId !== meData?.learnerId && (
                            <ExternalLinkButton
                              href="https://help.outthink.io/earn-badges/ "
                              text={<NamedContentEntryText container={'span'} refKey="leaderboard" subKey="viewAll" />}
                              className="view-all"
                            />
                          )}
                          {userData.learnerId === meData?.learnerId ? (
                            <ExternalLinkButton
                              href="https://help.outthink.io/earn-badges/ "
                              text={
                                <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="howToEarn" />
                              }
                              className="earn-badges"
                            />
                          ) : null}
                        </ExternalLinksContainer>
                      )}
                    />
                  );
                },
                alignment: 'left',
                width: '6fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="level" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <UserLevelWrapper>
                      <Tooltip
                        placement="left"
                        offset={15}
                        variant="light"
                        content={<LevelTooltip tooltipDetails={UserLevelDetails[userData.levelId]} />}
                      >
                        <UserLevel>
                          {UserLevelDetails[userData.levelId]?.level}{' '}
                          <LevelId>{UserLevelDetails[userData.levelId]?.name}</LevelId>
                        </UserLevel>
                      </Tooltip>
                    </UserLevelWrapper>
                  );
                },
                alignment: 'left',
                width: '200px',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="points" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <UserPoints className="user-points">
                      {formatNumberWithCommas(userData.cyberIqPoints)}{' '}
                      <Icon width={20} height={20} icon={IconType.COIN} />
                    </UserPoints>
                  );
                },
                alignment: 'right',
                width: '150px',
              },
            ],
      mobileColumns:
        selectedFilterScope === LeaderboardTypeParam.DEPARTMENT
          ? [
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="rank" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <UserRank rank={department.rank} previousRank={department.previousRank} />;
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="departments" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <Column>
                      <UserName>{department.department}</UserName>
                      <UserPoints>
                        {formatNumberWithCommas(department.averageScore)}{' '}
                        <Icon width={20} height={20} icon={IconType.COIN} />
                      </UserPoints>
                    </Column>
                  );
                },
                alignment: 'left',
                width: '2fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="managerTeamBehaviors" subKey="teamSize" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <Column>
                      <div style={{ textAlign: 'center' }}>{department.learnerCount} learners</div>
                    </Column>
                  );
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="badges" />,
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <Column>
                      <BadgeList badgeIds={department.badges ?? []} />
                    </Column>
                  );
                },
                alignment: 'right',
                width: '2fr',
              },
            ]
          : [
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="rank" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return <UserRank rank={userData.rank} previousRank={userData.previousRank} />;
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="name" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <Column>
                      <UserName>
                        {userData.avatar ? (
                          <UserImg src={`data:image/png;base64,${userData.avatar}`} />
                        ) : (
                          <AvatarInitials name={userData.name || ''} id={userData.name ?? ''} size="36px" />
                        )}
                        {userData.name}
                      </UserName>
                      <UserPoints>
                        {formatNumberWithCommas(userData.cyberIqPoints)}{' '}
                        <Icon width={20} height={20} icon={IconType.COIN} />
                      </UserPoints>
                    </Column>
                  );
                },
                alignment: 'left',
                width: '2fr',
              },
              {
                header: <NamedContentEntryText container={'span'} refKey="leaderboard" subKey="level" />,
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <Column>
                      <UserLevel>
                        <LevelId>{UserLevelDetails[userData.levelId]?.name}</LevelId>{' '}
                        <LevelNumber>{UserLevelDetails[userData.levelId]?.number}</LevelNumber>
                      </UserLevel>
                      <BadgeList badgeIds={userData.badges ?? []} />
                    </Column>
                  );
                },
                alignment: 'right',
                width: '2fr',
              },
            ],
    }),
    [meData?.learnerId, selectedFilterScope],
  );
};

const UserLevelWrapper = styled.div`
  width: 100%;
  height: calc(100% - 10px);
  display: flex;
  align-items: center;
  border-right: 1px solid ${({ theme }) => theme.colors.tableBorder};

  @media (max-width: 768px) {
    border: none;
  }
`;

const UserLevel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.userLevel};
  > b {
    text-transform: uppercase;
  }

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    padding-top: 8px;
  }
`;

const LevelNumber = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.userLevel};
  color: ${({ theme }) => theme.colors.primaryContrast};
`;

const LevelId = styled.div`
  font-weight: 600;
  letter-spacing: 0.24px;
  text-transform: uppercase;
`;

const UserPoints = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: row-reverse;
    padding: 10px 8px;
    width: 90px;
    justify-content: flex-end;
    border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
    background: ${({ theme }) => theme.colors.trainingCardBackground};
  }
`;

const UserImg = styled.img`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
`;

const UserName = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.heading};
`;

const Column = styled.div`
  height: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 8px;
`;

const ExternalLinksContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;
